/**
 * ADR-0185 Addendum 4 — the Optimize landing (`/app/optimize`) is ONE list,
 * every role; the Models/Boards tabs are retired. Each row names a model and
 * its standing plan; clicking a model lands on its home, which opens on the
 * Board tab. A caller without model-manage authority who has exactly one
 * active model never sees this page at all: they land straight on that
 * model's home.
 *
 * Rows render from the models read immediately; the standing-plan numbers
 * arrive from each model's board read and fill in behind them — the list
 * never waits on the fan-out. "+ New model" stays builder/admin, with the
 * template-tucked create flow.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { formatTimestamp } from "../lib/formatTime.js";
import {
  canManageModels,
  modelHomePath,
  optimizeLandingRedirect,
  optimizeListRedirect,
  type OptimizeModelRef,
} from "../lib/optimizeNav.js";
import type { BoardPayload } from "../components/board/boardTypes.js";

type ShippedTemplate = {
  id: string;
  display_name: string;
  description: string;
  version: number;
};

type ModelListItem = {
  id: string;
  name: string;
  template_display_name?: string | null;
  status: "active" | "disabled";
};

/**
 * One list row. The model's name, template kind and link come from the models
 * read and render at once; the standing-plan numbers arrive from that model's
 * board read and fill in behind them.
 */
type Row = {
  id: string;
  name: string;
  templateKind: string | null;
  status: "active" | "disabled";
  standing: "pending" | "unavailable" | "none" | "read";
  proposalId: string | null;
  version: number | null;
  solvedAt: string | null;
  hoursSaved: number | null;
  promisesAtRisk: number | null;
};

function standingLabel(standing: Row["standing"]): string {
  if (standing === "pending") return "…";
  if (standing === "unavailable") return "Unavailable";
  return "Not solved yet";
}

/** A cell with no number yet — still loading, or genuinely absent. */
function Absent({ row }: { row: Row }) {
  return <span className="muted">{row.standing === "pending" ? "…" : "—"}</span>;
}

export function OptimizeHome(props: { entitlement?: string; models: OptimizeModelRef[] }) {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const canManage = canManageModels(props.entitlement);

  const [rows, setRows] = useState<Row[] | null>(null);
  const [templates, setTemplates] = useState<ShippedTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [data, templateData] = await Promise.all([
        apiFetch<{ models: ModelListItem[] }>("/api/tenant/optimize/models"),
        // Templates feed the create flow alone — never fetched for callers
        // who cannot create.
        canManage
          ? apiFetch<{ templates: ShippedTemplate[] }>("/api/tenant/optimize/templates")
          : Promise.resolve({ templates: [] as ShippedTemplate[] }),
      ]);
      // Viewers see active models only; builders and admins see disabled
      // rows too — a disabled model must stay reachable by the people who
      // can re-enable it.
      const listed = canManage ? data.models : data.models.filter((m) => m.status === "active");
      setTemplates(templateData.templates);
      setRows(
        listed.map((m) => ({
          id: m.id,
          name: m.name,
          templateKind: m.template_display_name ?? null,
          status: m.status,
          standing: "pending",
          proposalId: null,
          version: null,
          solvedAt: null,
          hoursSaved: null,
          promisesAtRisk: null,
        }))
      );

      // The standing plan's numbers live on the composed board read, not on
      // the model list — one read per active model, in parallel, each landing
      // on its own row. A model whose board fails to compose says so in place
      // rather than taking the whole list down with it.
      await Promise.all(
        listed.map(async (m) => {
          let patch: Partial<Row>;
          try {
            const board = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${m.id}/board`);
            const standing = board.standing;
            patch = standing
              ? {
                  standing: "read",
                  proposalId: standing.id,
                  version: standing.version,
                  solvedAt: standing.solve?.finished_at ?? standing.created_at,
                  hoursSaved: standing.diff?.hours_saved ?? null,
                  promisesAtRisk: standing.diff?.promises_at_risk?.length ?? null,
                }
              : { standing: "none" };
          } catch {
            patch = { standing: "unavailable" };
          }
          setRows((prev) => prev?.map((r) => (r.id === m.id ? { ...r, ...patch } : r)) ?? prev);
        })
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create() {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      // The template is implicit while exactly one ships.
      await apiFetch("/api/tenant/optimize/models", {
        method: "POST",
        body: { name: newName.trim() },
      });
      toast.push({ kind: "success", message: "Model created." });
      setNewName("");
      setCreating(false);
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  // A `?tab=` search names tabs that no longer exist — land on the plain list.
  const listRedirect = optimizeListRedirect(searchParams);
  if (listRedirect) return <Navigate to={listRedirect} replace />;

  // The viewer fast path: one active model means its board IS the destination.
  const fastPath = optimizeLandingRedirect(props.entitlement, props.models);
  if (fastPath) return <Navigate to={fastPath} replace />;

  return (
    <PageLayout title="Optimize">
      <div className="card adminList">
        {error ? <ErrorState detail={error} /> : null}
        {!error && !rows ? <LoadingState /> : null}
        {!error && rows && rows.length === 0 ? (
          <div className="adminListEmpty">
            {canManage
              ? "No optimization models yet. A model binds your pipeline outputs to a scheduling problem and turns them into priced run-order proposals."
              : "No schedule to board yet — the board opens once an active optimization model exists."}
          </div>
        ) : null}
        {!error && rows && rows.length > 0 ? (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Template</th>
                  <th>Standing plan</th>
                  <th>Solved</th>
                  <th>Setup hours saved</th>
                  <th>Promises at risk</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link className="adminListName" to={modelHomePath(r.id)}>
                        {r.name}
                      </Link>
                      {r.status === "disabled" ? (
                        <span className="pill pillNeutral" style={{ marginLeft: 6 }}>Disabled</span>
                      ) : null}
                    </td>
                    <td>{r.templateKind ?? <span className="muted">—</span>}</td>
                    <td>
                      {r.version != null ? (
                        // The standing plan links to its detail for callers who
                        // can open it; everyone else gets the version as text.
                        canManage && r.proposalId ? (
                          <Link to={`/app/optimize/proposals/${r.proposalId}`}>v{r.version}</Link>
                        ) : (
                          `v${r.version}`
                        )
                      ) : (
                        <span className="muted">{standingLabel(r.standing)}</span>
                      )}
                    </td>
                    <td>{r.solvedAt ? formatTimestamp(r.solvedAt) : <Absent row={r} />}</td>
                    <td>{r.hoursSaved != null ? `${r.hoursSaved} h` : <Absent row={r} />}</td>
                    <td>
                      {r.promisesAtRisk == null ? (
                        <Absent row={r} />
                      ) : r.promisesAtRisk > 0 ? (
                        <span className="pill pillWarn">{r.promisesAtRisk}</span>
                      ) : (
                        <span className="pill pillOk">None</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {!error && rows && canManage ? (
          creating ? (
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {/* The template is implicit while exactly one ships — say what
                  the model will do, from the served projection. */}
              {templates.map((t) => (
                <div key={t.id}>
                  <strong>{t.display_name}</strong>
                  <div className="help">{t.description}</div>
                </div>
              ))}
              <div className="inputRow" style={{ gap: 8, alignItems: "center" }}>
                <input
                  className="input"
                  placeholder="Model name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={{ maxWidth: 260 }}
                />
                <button className="btn btnPrimary btnSm" onClick={() => void create()} disabled={busy || !newName.trim()}>
                  {busy ? "Creating…" : "Create"}
                </button>
                <button className="btn btnSm" onClick={() => setCreating(false)} disabled={busy}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <button className="adminNewLink" onClick={() => setCreating(true)} disabled={templates.length === 0}>
                + New model
              </button>
              {templates.length === 0 ? (
                <div className="help">
                  Models are created from templates shipped with the platform — none are shipped yet.
                </div>
              ) : null}
            </>
          )
        ) : null}
      </div>
    </PageLayout>
  );
}
